/* global React */
import { Grid } from '@mui/material'
import { type FC } from 'react'
import Text from 'ui/Element/Text'
import HyperDecoratedTextBase from 'ui/Element/Text/Hyper/Decorated'
import { type MessageClasses } from 'ui/Element/Message/List/Template/Message'

const HyperDecoratedText = HyperDecoratedTextBase as any

const EMOJI_ONLY = /^[\p{Emoji}\s]{1,5}$/u

export interface ContentBaseProps {
  /** the message text */
  text?: string
  /** whether the message has been deleted */
  isDeleted?: boolean
  /** whether the message has no displayable content */
  isEmpty?: boolean
  /** label shown for a deleted message */
  deletedLabel?: string
  /** label shown for an empty message */
  emptyLabel?: string
}

interface ContentProps extends ContentBaseProps {
  /** the message + messageEmoji css classes */
  classes: MessageClasses
}

/**
 * Content Component
 *
 * The message's text bubble — or, when the message has been deleted, the italic
 * deleted-message placeholder in its place. Emoji-only text is enlarged.
 *
 * @param props              the component's properties
 * @param props.text         the message text
 * @param props.isDeleted    whether the message has been deleted
 * @param props.isEmpty      whether the message has no displayable content
 * @param props.deletedLabel label shown for a deleted message
 * @param props.emptyLabel   label shown for an empty message
 * @param props.classes      the message + messageEmoji css classes
 * @returns                  the message content, or null
 */
const Content = ({ text, isDeleted, deletedLabel, isEmpty, emptyLabel, classes }: ContentProps) => {
  if (isDeleted || isEmpty) {
    return (
      <Text
        variant={'12/regular'}
        fontStyle={'italic'}
        color={'gray.700'}
        component={Text}
        // prevents text being cut off
        sx={{ paddingRight: '0.1875rem' }}
      >
        {isDeleted ? deletedLabel : emptyLabel}
      </Text>
    )
  }

  if (!text) return null

  const emojiClassName = EMOJI_ONLY.test(text) ? classes.messageEmoji : ''

  return (
    <Grid
      item
      className={`${classes.message} ${emojiClassName}`}
    >
      <HyperDecoratedText
        value={text}
        variant={'14/medium'}
      />
    </Grid>
  )
}

export default Content
